import { useGetServerTimeLazyQuery } from '@exam-center/graphql';
import { useCallback, useEffect, useRef } from 'react';

import { serverTimeStore } from './server-time-store';

const DEFAULT_SYNC_INTERVAL_MS = 5 * 60_000;

interface UseServerTimeSyncOptions {
  /** How often to re-sync (default: 5 minutes) */
  intervalMs?: number;
}

/**
 * Starts periodic server-time synchronization on mount.
 *
 * Uses the generated `ecGetServerTime` GraphQL query.
 * Writes the computed offset directly to `serverTimeStore` (module-level store).
 * This hook should be called once, near the top of the app.
 * It does **not** cause re-renders in its host component.
 */
export function useServerTimeSync(options?: UseServerTimeSyncOptions) {
  const { intervalMs = DEFAULT_SYNC_INTERVAL_MS } = options ?? {};
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const [fetchServerTime] = useGetServerTimeLazyQuery({ fetchPolicy: 'network-only' });

  const sync = useCallback(async () => {
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    try {
      serverTimeStore.setSyncing(true);

      const { data } = await fetchServerTime();

      const serverTimestamp = data?.ecGetServerTime.currentTime;

      if (!(serverTimestamp instanceof Date)) {
        throw new Error('Invalid server time response');
      }
      const offset = serverTimestamp.getTime() - Date.now();
      serverTimeStore.setOffset(offset);
    } catch (err) {
      console.error('Server time sync failed:', err);
      // Do not reset offset on failure — preserve the last known good offset
      // so the displayed time does not revert to the client's local clock.
    } finally {
      serverTimeStore.setSyncing(false);
      inFlightRef.current = false;
    }
  }, [fetchServerTime]);

  useEffect(() => {
    // Initial sync
    sync();

    timerRef.current = window.setInterval(sync, intervalMs);

    return () => {
      if (timerRef.current === null) return;

      window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [sync, intervalMs]);
}
